import React from "react";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import field from "./field.jpg";
import PESLogo from "../../icons/PES_Logo.svg";
import MagicWand from "../../icons/magic-wand-01.svg?react";
import GoogleIcon from "../../icons/google.svg?react";
import { useAuth } from "@components/auth-provider/AuthProvider";
import {
  PASSWORD_POLICY_DESCRIPTION,
  isPasswordValid,
} from "@modules/authentication/passwordPolicy";
import { useMutation } from "@apollo/client";
import {
  LoginWithPasswordDocument,
  LoginWithPasswordMutation,
  LoginWithPasswordMutationVariables,
  RequestMagicLinkDocument,
  RequestMagicLinkMutation,
  RequestMagicLinkMutationVariables,
  RequestPasswordResetDocument,
  RequestPasswordResetMutation,
  RequestPasswordResetMutationVariables,
  ResetPasswordWithCodeDocument,
  ResetPasswordWithCodeMutation,
  ResetPasswordWithCodeMutationVariables,
} from "generated/graphql";

type LoginMode = "login" | "reset";

const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

const Login = () => {
  const auth = useAuth();
  const [mode, setMode] = React.useState<LoginMode>("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [resetCode, setResetCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [resetCodeRequested, setResetCodeRequested] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [sendMagicLink, sendMagicLinkMutation] = useMutation<
    RequestMagicLinkMutation,
    RequestMagicLinkMutationVariables
  >(RequestMagicLinkDocument);
  const [loginWithPassword, loginWithPasswordMutation] = useMutation<
    LoginWithPasswordMutation,
    LoginWithPasswordMutationVariables
  >(LoginWithPasswordDocument);
  const [requestPasswordReset, requestPasswordResetMutation] = useMutation<
    RequestPasswordResetMutation,
    RequestPasswordResetMutationVariables
  >(RequestPasswordResetDocument);
  const [resetPasswordWithCode, resetPasswordWithCodeMutation] = useMutation<
    ResetPasswordWithCodeMutation,
    ResetPasswordWithCodeMutationVariables
  >(ResetPasswordWithCodeDocument);

  const normalizedEmail = email.trim().toLowerCase();
  const loading =
    sendMagicLinkMutation.loading ||
    loginWithPasswordMutation.loading ||
    requestPasswordResetMutation.loading ||
    resetPasswordWithCodeMutation.loading;

  const clearFeedback = () => {
    setMessage(null);
    setErrorMessage(null);
  };

  const handlePasswordLogin = async () => {
    clearFeedback();
    try {
      const result = await loginWithPassword({
        variables: { input: { email: normalizedEmail, password } },
      });

      if (!result.data?.loginWithPassword) {
        setErrorMessage("Invalid email or password.");
        return;
      }

      auth.setAuthToken(result.data.loginWithPassword);
    } catch {
      setErrorMessage("Invalid email or password.");
    }
  };

  const handleMagicLink = async () => {
    clearFeedback();
    try {
      const result = await sendMagicLink({
        variables: { input: { email: normalizedEmail } },
      });
      if (result.data?.requestMagicLink) {
        setMessage("Your magic link has been sent; please check your email.");
      } else {
        setErrorMessage(
          "There was an error sending your magic link. Please try again."
        );
      }
    } catch {
      setErrorMessage(
        "There was an error sending your magic link. Please try again."
      );
    }
  };

  const handleRequestReset = async () => {
    clearFeedback();
    try {
      const result = await requestPasswordReset({
        variables: { input: { email: normalizedEmail } },
      });
      if (result.data?.requestPasswordReset) {
        setResetCodeRequested(true);
        setMessage(
          "If the account exists, a reset code has been sent to that email address."
        );
      } else {
        setErrorMessage(
          "There was an error requesting a reset code. Please try again."
        );
      }
    } catch {
      setErrorMessage(
        "There was an error requesting a reset code. Please try again."
      );
    }
  };

  const handleResetPassword = async () => {
    clearFeedback();
    try {
      const result = await resetPasswordWithCode({
        variables: {
          input: {
            email: normalizedEmail,
            code: resetCode.trim(),
            password: newPassword,
          },
        },
      });

      if (!result.data?.resetPasswordWithCode) {
        setErrorMessage("The code or password was not accepted.");
        return;
      }

      auth.setAuthToken(result.data.resetPasswordWithCode);
    } catch {
      setErrorMessage("The code or password was not accepted.");
    }
  };

  const canRequestReset = isValidEmail(normalizedEmail) && !loading;
  const canResetPassword =
    canRequestReset &&
    resetCode.trim().length > 0 &&
    isPasswordValid(newPassword);
  const canPasswordLogin =
    isValidEmail(normalizedEmail) && password.length > 0 && !loading;
  const canSendMagicLink = isValidEmail(normalizedEmail) && !loading;

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center">
      <img
        src={field}
        alt="Field"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="rounded-xl border bg-card p-8 text-card-foreground shadow-lg">
          <div className="mb-6 flex flex-col items-center gap-2 text-center">
            <img
              src={PESLogo}
              alt="PES Logo"
              className="mb-4 w-[85px] dark:invert"
            />
            <h1 className="text-2xl font-semibold tracking-tight">Welcome</h1>
            <p className="text-sm text-muted-foreground">
              Sign in with your password or request a magic link.
            </p>
          </div>

          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (mode === "login" && canPasswordLogin) {
                void handlePasswordLogin();
              }
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  clearFeedback();
                }}
                disabled={loading}
                className="w-full"
              />
            </div>

            {mode === "login" ? (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      clearFeedback();
                    }}
                    disabled={loading}
                    className="w-full"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!canPasswordLogin}
                >
                  Sign in
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="px-0"
                  disabled={loading}
                  onClick={() => {
                    clearFeedback();
                    setMode("reset");
                  }}
                >
                  Forgot password?
                </Button>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      OR
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full gap-2"
                  onClick={() => void handleMagicLink()}
                  disabled={!canSendMagicLink}
                >
                  <MagicWand className="h-4 w-4" />
                  Send a magic link
                </Button>
              </>
            ) : (
              <>
                {!resetCodeRequested ? (
                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => void handleRequestReset()}
                    disabled={!canRequestReset}
                  >
                    Send reset code
                  </Button>
                ) : (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="reset-code">Reset code</Label>
                      <Input
                        id="reset-code"
                        inputMode="numeric"
                        placeholder="Enter the code from your email"
                        value={resetCode}
                        onChange={(event) => {
                          setResetCode(event.target.value);
                          clearFeedback();
                        }}
                        disabled={loading}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="new-password">New password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="Enter your new password"
                        value={newPassword}
                        onChange={(event) => {
                          setNewPassword(event.target.value);
                          clearFeedback();
                        }}
                        disabled={loading}
                      />
                      <p className="text-xs text-muted-foreground">
                        {PASSWORD_POLICY_DESCRIPTION}
                      </p>
                    </div>
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => void handleResetPassword()}
                      disabled={!canResetPassword}
                    >
                      Reset password
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="link"
                  className="px-0"
                  disabled={loading}
                  onClick={() => {
                    clearFeedback();
                    setMode("login");
                  }}
                >
                  Back to sign in
                </Button>
              </>
            )}

            {message && (
              <p className="text-sm text-muted-foreground">{message}</p>
            )}
            {errorMessage && (
              <p className="text-sm text-destructive">{errorMessage}</p>
            )}

            {mode !== "reset" && (
              <>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      OR
                    </span>
                  </div>
                </div>

                <Button variant="secondary" className="w-full gap-2" disabled>
                  <GoogleIcon className="h-4 w-4" />
                  Sign in with Google
                </Button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
